/**
 * Stable display constants for the CRO forecast feature.
 *
 * Backend mirrors are in `backend/app/features/cro_forecast/constants.py`.
 */

export const PIPELINES = {
  SALES: "Sales Pipeline",
  PARTNERSHIPS: "Partnerships",
} as const;

export type PipelineKey = (typeof PIPELINES)[keyof typeof PIPELINES];

/** All HubSpot pipelines we ever surface — kept stable so the Pipeline
 *  Movement filter chip lists the same options regardless of which
 *  pipeline the current week has activity in. */
export const ALL_PIPELINES: readonly string[] = [
  "Sales Pipeline",
  "Customer Success (Formally Upsells & Replacements)",
  "Reseller Deals",
  "Partnerships",
];

/** SQO-and-beyond stages in the Sales Pipeline. Pipeline Movement gates
 *  to deals that reached at least one of these (or the corresponding
 *  qualified stage in other pipelines). */
export const SALES_SQO_PLUS_STAGES: readonly string[] = [
  "Sales Qualified",
  "Technical Validation",
  "Contract Negotiation",
];

/** HubSpot portal id used to build deep-link URLs (Cinch's prod portal). */
const HUBSPOT_PORTAL_ID = "21771408";

/** Build the URL that opens a deal in HubSpot's CRM. Pattern matches
 *  the canonical record route HubSpot redirects to from any deal-id
 *  surface (Forecasting, search, etc.). */
export function hubspotDealUrl(dealId: string): string {
  return `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/record/0-3/${dealId}`;
}

/** 4-bucket display segments — match backend constants.DISPLAY_SEGMENTS.
 *  These render in the New Revenue / Churn & Contraction tables on the
 *  Summary tab. The 3-bucket rollup labels (Direct NL / Partner / CS
 *  Expansion) collapse these for the Plan vs Forecast vs Actual chart. */
export const SEGMENTS = [
  "direct_nl",
  "direct_reseller",
  "upsell_reseller",
  "upsell_replacement",
  "other",
] as const;
export type Segment = (typeof SEGMENTS)[number];

export const SEGMENT_LABEL: Record<Segment, string> = {
  direct_nl: "Direct NL",
  direct_reseller: "Direct Reseller",
  upsell_reseller: "Upsell Reseller",
  upsell_replacement: "Upsell & Replacement",
  other: "Other",
};

/** 3-bucket rollup labels — used by the Plan vs Forecast vs Actual chart. */
export const ROLLUP_SEGMENT_LABEL: Record<string, string> = {
  direct_nl: "Direct NL",
  partner: "Partner",
  cs_expansion: "CS Expansion",
};

/** UI quick-pick buttons in the deal-commit drawer. Free-form input also
 *  accepted in [0, 1]. */
export const PROBABILITY_PRESETS: readonly number[] = [0.1, 0.25, 0.5, 0.75, 0.9];

export const COMMIT_CATEGORIES: readonly {
  value: string;
  label: string;
}[] = [
  { value: "commit", label: "Commit" },
  { value: "best_case", label: "Best Case" },
  { value: "most_likely", label: "Most Likely" },
  { value: "pipeline", label: "Pipeline" },
  { value: "upside", label: "Upside" },
  { value: "omit", label: "Omit" },
];

/** Deal-source dot colors — mirrors the static HTML report so the brand feel
 *  carries across. */
export const SOURCE_COLOR: Record<string, string> = {
  Event: "#F2DFA0",
  "BDR - Outbound": "#9B8DD4",
  "Customer Referral": "#96CCBA",
  Inbound: "#E8A99A",
  "AE - Self Source": "#7ABFBF",
  Partner: "#F4AABB",
  "Employee Referral": "#C9C9C9",
};

/** Stage chip variants — each Sales-Pipeline stage gets a distinct tint so
 *  the Open Deals table reads at a glance. Variants come from the cinch
 *  design system; "success-solid" reserved for closed-won-eligible CN. */
export type StageVariant =
  | "info"
  | "warning"
  | "success"
  | "neutral"
  | "danger"
  | "info-solid"
  | "success-solid"
  | "warning-solid";

export const STAGE_VARIANT: Record<string, StageVariant> = {
  // Sales Pipeline — progression order: Demo → SQ → TV → CN
  "Demo - Scheduled": "neutral",
  "Sales Qualified": "info",
  "Technical Validation": "warning",
  "Contract Negotiation": "success-solid",
  // Reseller Deals pipeline
  "Reseller Qualified": "info",
  "Reseller Demo Scheduled": "neutral",
  "Registration Pending": "warning",
  // Partnerships pipeline (Gabe's)
  Exploration: "neutral",
  "Partner Qualified": "info",
  "Proposal / Negotiaton": "warning",
  // CS / Upsells pipeline
  Discovery: "neutral",
  "Verbal Yes": "warning",
  "Proof Of Concept In Progress": "info",
};

export function stageVariantFor(stage: string): StageVariant {
  return STAGE_VARIANT[stage] ?? "neutral";
}
